import * as React from "react";
import {FontAwesomeIcon}        from "@fortawesome/react-fontawesome";
import {faThumbsUp, faThumbsDown} from "@fortawesome/free-solid-svg-icons";
import {AiToolInteractionTrigger} from "./ai-tool-interaction-trigger";

import "./auto-text-generation-modal.scss";

import { cs }                                            from "@Common/react/chain-services";
import {ModalService3}                                   from "@Common/ui-components/modal-service-3/modal-service-3";
import {UseState}                                        from "@Common/react/use-state";
import {Load2}                                           from "@Common/react/load2";
import {consumeContext}                                  from "@Common/react/context";
import { ButtonSpinner }                                 from "loading-spinners/button-spinner";
import { ScreenLoader }                                  from "loading-spinners/screen-loader";
import { TextGenerationFeedback }                        from "./text-generation-feedback";


export const AutoTextGenerationModal = ({ idOrder, maxCharOverride=false, maxChars, setFieldVal, next: rootNext }) => cs(
    consumeContext("session"),
    consumeContext("company"),
    ["modal", ({}, next) => ModalService3({
        next: ({close}) => next({close}),
        xToClose: true,
        ignorePassiveClose: true,
        rootNext,
        contentClassName: 'auto-text-generation-modal-2j4',
        xdQaName: "TextGenerationModal",
    })],
    consumeContext("apis"),
    ["userCue", (_, next) => UseState({initValue: "", next})],
    ["changeHandler", ({userCue}, next) => next( (event) => userCue.onChange(event.target.value) )],
    ["generatedText", ({}, next) => UseState({initValue: "", next})],
    ["apiLoading", ({}, next) => UseState({initValue: false, next})],
    ["resetAllVals", ({userCue, generatedText, apiLoading}, next) => next( () => {
        userCue.onChange("");
        generatedText.onChange("");
        apiLoading.onChange(false);
    })],
    ["aiFailureMessage", ({}, next) => next("We apologize, but there was an issue or delay with generating the text. If you wish to continue, please click \"Generate Text\" again.")],
    ["suggestedCues", ({}, next) => next(
        ["Generate a captivating description for a luxury penthouse in downtown.",
        "Craft an attention-grabbing headline for a charming suburban home.",
        "Create a persuasive call to action for a stunning beachfront property.",
        "Generate an enticing property overview for a spacious family residence.",
        "Craft a unique selling point for a cozy cottage in the countryside.",
        "Generate a catchy opening line for a modern urban loft.",
        "Create a vivid description for an elegant townhouse near schools.",
        "Craft a compelling feature highlight for a renovated historical home.",
        "Generate an engaging intro for a lakeside property with panoramic views.",
        "Create an attractive teaser for a contemporary apartment in the heart of the city.",
        "Craft a standout headline for a sprawling ranch with ample outdoor space.",
        "Generate a dynamic call to action for an energy-efficient suburban home.",
        "Create an emotion-evoking description for a mountain retreat.",
        "Craft a captivating introduction for a property with a private garden oasis.",
        "Generate an attention-grabbing tagline for a high-rise condo with amenities.",
        "Create an inviting opening for a family-friendly neighborhood home.",
        "Craft a memorable selling point for a property with a home office.",
        "Generate a compelling narrative for a property near vibrant nightlife.",
        "Craft a persuasive feature highlight for a property with a pool.",
        "Create an enticing snippet for a property with a chef's kitchen."]
    )],
    ["randomSuggestedCue", ({suggestedCues}, next) => Load2({
        fetch: () => suggestedCues[Math.floor(Math.random() * 20)],
        next
    })],
    ["gptResponseState", ({}, next) => UseState({initValue: null, next})],
    ["showFeedbackInput", ({}, next) => UseState({initValue: false, next})],
    ["feedbackSent", ({}, next) => UseState({initValue: false, next})],


    ({session, modal, apis, userCue, changeHandler, generatedText, apiLoading, resetAllVals, aiFailureMessage, randomSuggestedCue, gptResponseState, showFeedbackInput, feedbackSent, company}) => (
        <div className="auto-text-generation-modal">
            <div className="input-container">
                <div className="header">
                    What can we help you write?
                </div>
                <hr/>
                <div>
                    <div className="header-text" xd-qa-type='label' xd-qa-name='label of AI model'>
                        Use 5 or more words to tell us what we should write about.
                    </div>
                    {!apiLoading.value ?
                        <textarea
                            className="user-cue-textbox"
                            value={userCue.value}
                            onChange={changeHandler}
                            placeholder={randomSuggestedCue.value}
                            style={generatedText.value.length ? {height: "200px"} : {}}
                            xd-qa-type="textarea"
                            xd-qa-name="Description"
                        />
                    :
                        <div className="generated-text-box" xd-qa-type='label' xd-qa-name='result of AI'>
                            {userCue.value}
                        </div>
                    }
                </div>
                <div className="generate-text-button-container">
                    <div className={apiLoading.value ? "generate-text-button generate-text-button-loading" : "generate-text-button"}
                        style={apiLoading.value ? {cursor: "not-allowed"} : {}}
                        xd-qa-type="button"
                        xd-qa-name="Generate New Text"
                        onClick={async () => {
                            generatedText.onChange("");
                            feedbackSent.onChange(false);
                            showFeedbackInput.onChange(false);
                            if ( userCue.value.length < 15 ) {
                                alert("please provide more text to help us generate better copy")
                            } else if (!apiLoading.value) {
                                apiLoading.onChange(true);
                                let timeout, failureValue= aiFailureMessage, timeLimit=60000;
                                const timeoutPromise = new Promise((resolve, reject) => {
                                    timeout = setTimeout(() => {
                                        resolve(failureValue);
                                    }, timeLimit);
                                });
                                let task =  apis.next.gpt ? apis.next.gpt.autoGeneratedText({"userCue": userCue.value, "maxChars": maxChars, "maxCharOverride": maxCharOverride}) : apis.next?.gpt?.autoGeneratedText({"userCue": userCue.value, "maxChars": maxChars, "maxCharOverride": maxCharOverride});
                                let gptResponse = await Promise.race([task, timeoutPromise]);
                                AiToolInteractionTrigger();
                                apiLoading.onChange(false);
                                if (typeof gptResponse ==='object') {
                                    if("choices" in gptResponse) {
                                        generatedText.onChange(gptResponse.choices[0].message.content);
                                        gptResponseState.onChange({
                                            "toolUsed": "Auto Generated Text",
                                            "idOrder": idOrder,
                                            "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                                            "userCue": userCue.value,
                                            "gptResponse": gptResponse.choices[0].message.content,
                                            "responseCode": gptResponse.choices[0].finish_reason
                                        });
                                        await apis.next.gpt.logAiInteraction({
                                            "toolUsed": "Auto Generated Text",
                                            "idOrder": idOrder,
                                            "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                                            "model": gptResponse.requestParams.model,
                                            "fullPrompt": gptResponse.requestParams.messages,
                                            "userCue": userCue.value,
                                            "gptResponse": gptResponse.choices[0].message.content,
                                            "responseCode": gptResponse.choices[0].finish_reason,
                                            "promptTokens": gptResponse.usage.prompt_tokens,
                                            "completionTokens": gptResponse.usage.completion_tokens
                                        });
                                    } else if("error" in gptResponse){
                                        generatedText.onChange(failureValue);
                                        gptResponseState.onChange({
                                            "toolUsed": "Auto Generated Text",
                                            "idOrder": idOrder,
                                            "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                                            "userCue": userCue.value,
                                            "gptResponse": gptResponse.error.message,
                                            "responseCode": gptResponse.error.type
                                        });
                                        await apis.next.gpt.logAiInteraction({
                                            "toolUsed": "Auto Generated Text",
                                            "idOrder": idOrder,
                                            "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                                            "model": gptResponse.requestParams.model,
                                            "fullPrompt": gptResponse.requestParams.messages,
                                            "userCue": userCue.value,
                                            "gptResponse": gptResponse.error.message,
                                            "responseCode": gptResponse.error.type
                                        });
                                    } else {
                                        generatedText.onChange(gptResponse);
                                        gptResponseState.onChange({
                                            "toolUsed": "Auto Generated Text",
                                            "idOrder": idOrder,
                                            "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                                            "gptResponse": "timeout",
                                            "userCue": userCue.value,
                                            "responseCode": "timeout"
                                        });
                                        await apis.next.gpt.logAiInteraction({
                                            "toolUsed": "Auto Generated Text",
                                            "idOrder": idOrder,
                                            "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                                            "gptResponse": "timeout",
                                            "userCue": userCue.value,
                                            "responseCode": "timeout"
                                        });
                                    }
                                } else {
                                    generatedText.onChange(gptResponse);
                                    gptResponseState.onChange({
                                        "toolUsed": "Auto Generated Text",
                                        "idOrder": idOrder,
                                        "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                                        "gptResponse": "timeout",
                                        "userCue": userCue.value,
                                        "responseCode": "timeout"
                                    });
                                    await apis.next.gpt.logAiInteraction({
                                        "toolUsed": "Auto Generated Text",
                                        "idOrder": idOrder,
                                        "userDetails": {"idCompany": company.idCompany, "idBranch": session.data.agent.idBranch, "idAgent": session.data.agent.idAgent},
                                        "gptResponse": "timeout",
                                        "userCue": userCue.value,
                                        "responseCode": "timeout",
                                    });
                                }
                            }
                        }}
                    >
                        <>
                            <span>{apiLoading.value ? <ButtonSpinner spinnerColor="white"/> : <img src='/next_images/ai-tools/ai-sparkles.svg' width="20px"/>}</span>Generate Text
                        </>
                    </div>
                </div>
            </div>
            <hr/>
                    {apiLoading.value ?
                        <>
                            <div className="generatied-text-loading">
                                <ScreenLoader loaderColor="white"/>
                            </div>
                            <div className="insert-button-container">
                                <div className="insert-button insert-button-inactive" xd-qa-type="button" xd-qa-name="Insert"
                                    onClick={() => {
                                        if (generatedText.value.length) {
                                            setFieldVal(generatedText.value);
                                            modal.close();
                                        }
                                    }}
                                >
                                    Insert
                                </div>
                            </div>
                        </>
                        : null
                    }
                    {generatedText.value.length ?
                        <>
                            <div className="output-container">
                                <div className="output-header" xd-qa-type="label" xd-qa-name="Output">
                                    Output
                                </div>
                                <div className={ generatedText.value == aiFailureMessage ?
                                "generated-text-box text-generation-error" : "generated-text-box"}>
                                    {generatedText.value}
                                </div>
                            </div>
                            <div className="insert-button-container">
                                <div className={(generatedText.value.length && generatedText.value !== aiFailureMessage) ? "insert-button" : "insert-button insert-button-inactive"}
                                    xd-qa-type="button" xd-qa-name="Insert button"
                                    onClick={() => {
                                        if (generatedText.value.length && generatedText.value !== aiFailureMessage) {
                                            setFieldVal(generatedText.value);
                                            modal.close();
                                        }
                                    }}
                                >
                                    Insert
                                </div>
                                <div className="feedback-cont">
                                    {!feedbackSent.value ? <>
                                        <div xd-qa-type="label" xd-qa-name="FEEDBACK">Feedback</div>
                                        <div className="feedback-thumbs-cont">
                                                <FontAwesomeIcon icon={faThumbsUp} size="lg" className="feedback-thumb" onClick={() => {
                                                    if (showFeedbackInput.value) return;
                                                    gptResponseState.onChange({
                                                        ...gptResponseState.value,
                                                        feedbackType: "positive",
                                                        setFeedbackSent: () => {feedbackSent.onChange(true); showFeedbackInput.onChange(false);},
                                                        closeModal: () => {showFeedbackInput.onChange(false);}
                                                    });
                                                    showFeedbackInput.onChange(true);
                                                }}/>
                                                <FontAwesomeIcon icon={faThumbsDown} size="lg" className="feedback-thumb"  onClick={() => {
                                                    if (showFeedbackInput.value) return;
                                                    gptResponseState.onChange({
                                                        ...gptResponseState.value,
                                                        feedbackType: "negative",
                                                        setFeedbackSent: () => {feedbackSent.onChange(true); showFeedbackInput.onChange(false);},
                                                        closeModal: () => {showFeedbackInput.onChange(false);}
                                                    });
                                                    showFeedbackInput.onChange(true);
                                                }}/>
                                            <div></div>
                                        </div>
                                    </> :
                                        <div className="feedback-sent-cont">
                                            <div className="feedback-sent" xd-qa-type="label" xd-qa-name="FEEDBACK SENT">FEEDBACK SENT</div>
                                        </div>
                                    }
                                </div>
                                {showFeedbackInput.value && TextGenerationFeedback({...gptResponseState.value})}
                            </div>
                        </>
                        : null
                    }
        </div>
    )

)